import { request } from "$/lib/api"
import { mechanicQueries } from "$/lib/queries/mechanic-queries"
import {
  ChangeStoreAccountLink,
  CreateStoreAccount,
  LinkStoreAccount,
  Route,
  StoreAccountOverview,
  UpdateStoreAccount,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

const fetchOverview = (storeId: string): Promise<StoreAccountOverview> =>
  request<StoreAccountOverview>({
    url: populateRoute(Route.STORE_ACCOUNT_CONTROLLER__FIND_OVERVIEW, storeId),
    method: "GET",
  })

const createAccount = (storeId: string, data: CreateStoreAccount) =>
  request<string>({
    url: populateRoute(Route.STORE_ACCOUNT_CONTROLLER__CREATE, storeId),
    method: "POST",
    data,
  })

const linkAccount = (storeId: string, data: LinkStoreAccount) =>
  request<void>({
    url: populateRoute(Route.STORE_ACCOUNT_CONTROLLER__LINK, storeId),
    method: "POST",
    data,
  })

const changeLink = (
  storeId: string,
  mechanicId: string,
  data: ChangeStoreAccountLink,
) =>
  request<void>({
    url: populateRoute(
      Route.STORE_ACCOUNT_CONTROLLER__CHANGE_LINK,
      storeId,
      mechanicId,
    ),
    method: "PATCH",
    data,
  })

const updateAccount = (
  storeId: string,
  mechanicId: string,
  data: UpdateStoreAccount,
) =>
  request<void>({
    url: populateRoute(
      Route.STORE_ACCOUNT_CONTROLLER__UPDATE,
      storeId,
      mechanicId,
    ),
    method: "PATCH",
    data,
  })

const setInactivated = (
  storeId: string,
  mechanicId: string,
  inactivated: boolean,
) =>
  request<void>({
    url: populateRoute(
      inactivated
        ? Route.STORE_ACCOUNT_CONTROLLER__INACTIVATE
        : Route.STORE_ACCOUNT_CONTROLLER__REACTIVATE,
      storeId,
      mechanicId,
    ),
    method: "POST",
  })

const deleteUnlinkedMechanic = (storeId: string, mechanicId: string) =>
  request<void>({
    url: populateRoute(
      Route.STORE_ACCOUNT_CONTROLLER__DELETE_UNLINKED_MECHANIC,
      storeId,
      mechanicId,
    ),
    method: "DELETE",
  })

const deleteUnlinkedAccount = (storeId: string, userId: string) =>
  request<void>({
    url: populateRoute(
      Route.STORE_ACCOUNT_CONTROLLER__DELETE_UNLINKED_ACCOUNT,
      storeId,
      userId,
    ),
    method: "DELETE",
  })

const storeAccountQueries = {
  all: () => ["storeAccount"],
  overview: (storeId: string) =>
    queryOptions({
      queryKey: [...storeAccountQueries.all(), "overview", storeId],
      queryFn: () => fetchOverview(storeId),
    }),
}

export function useStoreAccountMutations(storeId: string) {
  const queryClient = useQueryClient()

  // Names and the active set feed every staff picker, so refresh those too
  const invalidateQueries = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: storeAccountQueries.all() }),
      queryClient.invalidateQueries({ queryKey: mechanicQueries.all() }),
    ])

  const createMutation = useMutation({
    mutationFn: (data: CreateStoreAccount) => createAccount(storeId, data),
    onSettled: invalidateQueries,
  })

  const linkMutation = useMutation({
    mutationFn: (data: LinkStoreAccount) => linkAccount(storeId, data),
    onSettled: invalidateQueries,
  })

  const changeLinkMutation = useMutation({
    mutationFn: ({
      mechanicId,
      data,
    }: {
      mechanicId: string
      data: ChangeStoreAccountLink
    }) => changeLink(storeId, mechanicId, data),
    onSettled: invalidateQueries,
  })

  const updateMutation = useMutation({
    mutationFn: ({
      mechanicId,
      data,
    }: {
      mechanicId: string
      data: UpdateStoreAccount
    }) => updateAccount(storeId, mechanicId, data),
    onSettled: invalidateQueries,
  })

  const setInactivatedMutation = useMutation({
    mutationFn: ({
      mechanicId,
      inactivated,
    }: {
      mechanicId: string
      inactivated: boolean
    }) => setInactivated(storeId, mechanicId, inactivated),
    onSettled: invalidateQueries,
  })

  const deleteUnlinkedMechanicMutation = useMutation({
    mutationFn: (mechanicId: string) =>
      deleteUnlinkedMechanic(storeId, mechanicId),
    onSettled: invalidateQueries,
  })

  const deleteUnlinkedAccountMutation = useMutation({
    mutationFn: (userId: string) => deleteUnlinkedAccount(storeId, userId),
    onSettled: invalidateQueries,
  })

  return {
    createMutation,
    linkMutation,
    changeLinkMutation,
    updateMutation,
    setInactivatedMutation,
    deleteUnlinkedMechanicMutation,
    deleteUnlinkedAccountMutation,
  }
}

export default storeAccountQueries
